import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { 
  Table, 
  Button, 
  Spin, 
  Card, 
  Typography, 
  Space,
  Alert,
  Row,
  Col,
  Statistic
} from 'antd';
import { ArrowLeftOutlined, FileExcelOutlined, FilePdfOutlined } from '@ant-design/icons';
import { withAuthenticationRequired, useAuth0 } from "@auth0/auth0-react";
import { PageTitle, StandardCard, ExportExcelButton, ExportPDFButton } from '../../../components/common';
import { theme } from '../../../theme';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const { Title, Text } = Typography;

interface DetalleMetricaProps {
  /** Título de la métrica */
  titulo: string;
  /** Descripción de la métrica */
  descripcion?: string;
  /** Función para obtener los datos detallados */
  fetchData: (idEmpresa: number, idProducto: number, token: string) => Promise<any[]>;
  /** Columnas de la tabla */
  columns: any[];
  /** Nombre del archivo para exportación */
  nombreArchivo: string;
}

const DetalleMetrica: React.FC<DetalleMetricaProps> = ({
  titulo,
  descripcion,
  fetchData,
  columns,
  nombreArchivo
}) => {
  const { getAccessTokenSilently, user } = useAuth0();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState<number>(0);

  const productoId = searchParams.get('producto');
  const idEmpresa = user?.id_empresa;

  useEffect(() => {
    const cargarDatos = async () => {
      if (!idEmpresa || !productoId) {
        setError('Faltan parámetros necesarios (empresa o producto)');
        setLoading(false);
        return;
      }

      // Validar que productoId sea un número válido
      const productoIdNum = parseInt(productoId, 10);
      if (isNaN(productoIdNum) || productoIdNum <= 0) {
        setError(`ID de producto inválido: ${productoId}`);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const token = await getAccessTokenSilently();
        const datos = await fetchData(idEmpresa, productoIdNum, token);
        setData(datos);
        setTotal(datos.length);
      } catch (err) {
        console.error('Error al cargar datos:', err);
        setError(err instanceof Error ? err.message : 'Error desconocido al cargar los datos');
      } finally {
        setLoading(false);
      }
    };

    cargarDatos();
  }, [idEmpresa, productoId, getAccessTokenSilently, fetchData]);

  const handleVolver = () => {
    navigate('/reportes/compras');
  };

  const handleExportExcel = () => {
    if (data.length === 0) return;

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Detalle');
    XLSX.writeFile(wb, `${nombreArchivo}.xlsx`);
  };

  const handleExportPDF = () => {
    if (data.length === 0) return;

    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text(titulo, 14, 20);
    
    if (descripcion) {
      doc.setFontSize(12);
      doc.text(descripcion, 14, 30);
    }

    const tableData = data.map((item, index) => 
      columns.map(col => {
        const value = col.dataIndex ? item[col.dataIndex] : (col.render ? col.render(item[col.dataIndex], item, index) : '');
        return typeof value === 'object' ? JSON.stringify(value) : String(value || '');
      })
    );

    (doc as any).autoTable({
      head: [columns.map(col => col.title)],
      body: tableData,
      startY: descripcion ? 35 : 25,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [45, 92, 146] }
    });

    doc.save(`${nombreArchivo}.pdf`);
  };

  return (
    <div style={{ padding: '0' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <Space style={{ marginBottom: '16px' }}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={handleVolver}
            size="middle"
          >
            Volver
          </Button>
        </Space>
        <PageTitle level={3} style={{ fontSize: '22px', marginBottom: '8px' }}>
          {titulo}
        </PageTitle>
        {descripcion && (
          <Text type="secondary" style={{ fontSize: '14px' }}>
            {descripcion}
          </Text>
        )}
      </div>

      {/* Resumen */}
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={8}>
          <Card>
            <Statistic
              title="Total de Registros"
              value={total}
              valueStyle={{ color: theme.colors.primary }}
            />
          </Card>
        </Col>
      </Row>

      {/* Tabla */}
      <StandardCard
        title="Detalle"
        extra={
          <Space>
            <ExportExcelButton
              onClick={handleExportExcel}
              disabled={data.length === 0 || loading}
            />
            <ExportPDFButton
              onClick={handleExportPDF}
              disabled={data.length === 0 || loading}
            />
          </Space>
        }
      >
        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            style={{ marginBottom: '16px' }}
          />
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
            <div style={{ marginTop: '16px' }}>
              <Text>Cargando datos...</Text>
            </div>
          </div>
        ) : data.length === 0 ? (
          <Alert
            message="Sin datos"
            description="No se encontraron registros para esta métrica."
            type="info"
            showIcon
          />
        ) : (
          <Table
            columns={columns}
            dataSource={data.map((item, index) => ({ ...item, key: index }))}
            pagination={{
              pageSize: 50,
              showSizeChanger: true,
              showTotal: (total) => `${total} registros`,
              pageSizeOptions: ['10', '25', '50', '100']
            }}
            scroll={{ y: 'max(150px, calc(100vh - 400px))', x: 'max-content' }}
            size="middle"
          />
        )}
      </StandardCard>
    </div>
  );
};

export default withAuthenticationRequired(DetalleMetrica);

